import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import JobAlertsCard from '../components/JobAlertsCard';
import { ArrowLeft, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, checkAuth } = useAuth();
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [address, setAddress] = useState(user?.correspondence_address || '');

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          phone,
          correspondence_address: address,
        }),
      });
      if (res.ok) {
        toast.success('Profil zaktualizowany!');
        await checkAuth();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Nie udało się zapisać');
      }
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />Powrót
          </Button>
          <h1 className="font-heading font-bold text-2xl text-white">Mój profil</h1>
          <p className="text-slate-400 mt-1">Uzupełnij swoje dane kontaktowe</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <User className="w-5 h-5" />Dane osobowe
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Imię</Label>
                  <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Jan" data-testid="input-first-name" />
                </div>
                <div>
                  <Label htmlFor="lastName">Nazwisko</Label>
                  <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Kowalski" data-testid="input-last-name" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={user?.email || ''} disabled className="bg-slate-50" data-testid="input-email" />
                <p className="text-xs text-slate-500 mt-1">Email nie może być zmieniony</p>
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+48 123 456 789" data-testid="input-phone" />
              </div>
              <div>
                <Label htmlFor="address">Adres do korespondencji</Label>
                <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="ul. Przykładowa 1, 00-000 Warszawa" data-testid="input-address" />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-profile-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Zapisywanie...</> : 'Zapisz zmiany'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <JobAlertsCard />
      </div>
    </div>
  );
};

export default Profile;
